import React from 'react';
import styled from 'styled-components';

import { addMeasureType } from '~src/utils';
import { media } from '~src/styles/media-templates';

import * as icons from './svg.index';

export type SvgIconName = keyof typeof icons;

export const SvgContainer = styled.div<{ width: number | string; height?: number | string }>`
  width: ${({ width }) => addMeasureType(width)};
  height: ${({ height, width }) => addMeasureType(height || width)};

  svg {
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none; /* ie11 */
  }

  &.iconStar {
    position: relative;
    fill: none;
    stroke: ${({ theme }) => theme.baseBlue.a10};
    stroke-width: 1.2px;
    ${media.phone`
      height: 18px;
      width: 18px;
    `}

    &.glow::after {
      content: '';
      width: 20%;
      height: 20%;
      box-shadow: 0 0 14px 5px ${({ theme }) => theme.gold.a4};
      border-radius: 50%;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-60%, -20%);
    }
  }

  &.iconCheckbox svg,
  &.iconCheckboxPartial svg {
    color: ${({ theme }) => theme.white.a10};
    fill: ${({ theme }) => theme.baseBlue.a10};
  }

  &.iconCheckbox {
    svg {
      stroke: ${({ theme }) => theme.baseBlue.a10};
    }

    &.unchecked svg {
      fill: ${({ theme }) => theme.white.a10};
      stroke: ${({ theme }) => theme.baseBlue.a6};
    }

    &.disabled {
      svg {
        fill: #d3d3d3;
        stroke: #d3d3d3;
      }
      &.unchecked svg {
        color: #d3d3d3;
      }
    }
  }

  &.iconInfo {
    color: ${({ theme }) => theme.baseBlue.a6};
  }

  &.iconRefresh {
    stroke-width: 0.5;
  }

  &.iconExit,
  &.iconCircleQuestion,
  &.iconCircleStar,
  &.iconTiles,
  &.iconSettings,
  &.iconSearch {
    color: ${({ theme }) => theme.baseBlue.a6};
  }
  &.iconDropDownArrow {
    stroke: ${({ theme }) => theme.baseBlue.a6};
  }

  &.iconOutBox,
  &.iconSquare10,
  &.iconInfinity,
  &.iconCircleClock,
  &.iconRepeat,
  &.icon360,
  &.iconTopDown,
  &.iconBottomUp,
  &.iconReports,
  &.iconPulse,
  &.iconBin,
  &.iconPen,
  &.iconCsv,
  &.iconPulse {
    color: ${({ theme }) => theme.baseBlue.a10};
  }

  &.iconScale {
    fill: ${({ theme }) => theme.baseBlue.a6};
  }

  &.iconRadio {
    fill: ${({ theme }) => theme.baseBlue.a10};
    stroke-width: 1px;

    &.unchecked {
      fill: ${({ theme }) => theme.white.a10};
      color: ${({ theme }) => theme.baseBlue.a6};
    }
  }

  &.iconFacebook,
  &.iconTwitter,
  &.iconYoutube,
  &.iconInstagram,
  &.iconXing {
    color: ${({ theme }) => theme.white.a10};
    stroke: ${({ theme }) => theme.white.a10};
    fill: transparent;
  }

  &.iconCrossInCircle {
    color: ${({ theme }) => theme.baseBlue.a6};
    fill: ${({ theme }) => theme.baseBlue.a2};
  }
  &.iconTopicLabel {
    color: ${({ theme }) => theme.baseBlue.a10};
    fill: ${({ theme }) => theme.baseBlue.a10};
    opacity: 0.8;
  }
`;

interface Props {
  name: SvgIconName;
  className?: string;
  width?: number | string;
  height?: number | string;
  viewBoxWidth?: number;
  viewBoxHeight?: number;
}

const SvgIcon: React.FC<Props> = React.memo(
  ({ name, className = '', width = '25px', height = '', viewBoxWidth, viewBoxHeight}) => {
    const IconComp = icons[name];
    let iconEl = null;
    const vbWidth = viewBoxWidth || width;
    const vbHeight = viewBoxHeight || vbWidth;

    if (IconComp) {
      iconEl = (
        <SvgContainer className={`${name} ${className}`} width={width} height={height} role="img">
          <IconComp viewBox={`0 0 ${vbWidth} ${vbHeight}`.replace(/px/g, '')} />
        </SvgContainer>
      );
    } else {
      throw new Error(`There is no icon available with such name: [${name}].`);
    }
    return iconEl;
  },
);

export default SvgIcon;
